export const RECAPTCHA_SITE_KEY = "6Le7dXgUAAAAAKD_vdTFFmWPA6-4wTwNsU2WpTC6";

const RECAPTCHA_SCRIPT_ID = "recaptcha-script";
const RECAPTCHA_SCRIPT_URL = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;

/**
 * Adds the reCAPTCHA v3 script to the page on first use.
 *
 * reCAPTCHA is ~870 KB that only the login and sign-up flows need, so it is no longer a global
 * `<script>` in `index.html`. Routes with `meta.preloadRecaptcha` call this on entry (see
 * `router.ts`), so the script can collect its risk signals before the form is submitted, and
 * `getRecaptchaToken` calls it again as a fallback.
 *
 * The state lives in the page rather than in this module — the script element and the
 * `grecaptcha` global — so concurrent callers share one script. A script that did not provide
 * `grecaptcha` (a network error, or a blocker answering with an empty script) is removed, so the
 * page only ever holds a script that is still loading and the next call retries instead of waiting
 * on an element whose `load` already fired.
 *
 * @returns A promise that resolves once `grecaptcha` is available.
 */
export function loadRecaptcha(): Promise<void> {
  if (typeof window.grecaptcha !== "undefined") return Promise.resolve();

  let script = document.getElementById(RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement("script");
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = RECAPTCHA_SCRIPT_URL;
    script.async = true;
    document.head.appendChild(script);
  }
  const pendingScript = script;

  return new Promise((resolve, reject) => {
    const fail = () => {
      pendingScript.remove();
      reject(new Error("Failed to load reCAPTCHA"));
    };
    pendingScript.addEventListener(
      "load",
      () => (typeof window.grecaptcha !== "undefined" ? resolve() : fail()),
      { once: true }
    );
    pendingScript.addEventListener("error", fail, { once: true });
  });
}

/**
 * Requests a reCAPTCHA v3 token, loading the script first when the page has not loaded it yet.
 *
 * @returns The token to send in the `g-recaptcha-response` header.
 */
export async function getRecaptchaToken(): Promise<string> {
  await loadRecaptcha();
  return new Promise((resolve, reject) => {
    grecaptcha.ready(async () => {
      try {
        const token = await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: "action_name" });
        resolve(token);
      } catch (error) {
        reject(error);
      }
    });
  });
}
